"use client";

/* -------------------------------------------------------------------------- */
/* ImpactChip                                                                  */
/* -------------------------------------------------------------------------- */

export interface ImpactChipRef {
  oppId: string;
  oppTitle: string;
}

export interface ImpactChipProps {
  /**
   * Opportunities that reference this field.  Pass an empty array (or omit)
   * to render nothing.
   */
  refs: ImpactChipRef[];
  /**
   * `true`  → the field has a value (satisfied); renders green with a
   *           checkmark icon.
   * `false` → the field is missing (blocking); renders amber with an info icon.
   */
  provided: boolean;
}

/**
 * Tiny inline chip that indicates how many opportunities use (or are blocked
 * by) a given org-profile field.  Returns `null` when `refs` is empty.
 */
export function ImpactChip({ refs, provided }: ImpactChipProps) {
  if (refs.length === 0) return null;

  const titles = refs.map((r) => r.oppTitle).join(", ");
  const label = `${refs.length} opp${refs.length === 1 ? "" : "s"}`;
  const tooltipPrefix = provided ? "Used by" : "Blocking";

  return (
    <span
      title={`${tooltipPrefix}: ${titles}`}
      className={[
        "inline-flex items-center gap-[3px]",
        "h-[16px] px-[5px]",
        "rounded-[3px] border",
        "font-mono text-[9px] font-medium",
        "uppercase tracking-[0.08em]",
        "select-none",
        provided
          ? [
              "bg-[color-mix(in_oklab,var(--accent-teal)_12%,transparent)]",
              "text-[var(--accent-teal-text,var(--accent-teal))]",
              "border-[color-mix(in_oklab,var(--accent-teal)_30%,transparent)]",
            ].join(" ")
          : [
              "bg-[color-mix(in_oklab,var(--accent-gold)_15%,transparent)]",
              "text-[var(--accent-gold-text,var(--fg-secondary))]",
              "border-[color-mix(in_oklab,var(--accent-gold)_40%,transparent)]",
            ].join(" "),
      ].join(" ")}
    >
      {provided ? (
        /* checkmark */
        <svg
          aria-hidden
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        /* info circle */
        <svg
          aria-hidden
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      )}
      Used by {label}
    </span>
  );
}
